import { Session } from "@supabase/supabase-js";
import { getSupabaseClient } from "../../config/supabase";
import { DialogMessage } from "../types/Authentication";

/**
 * Gets authenticated headers for API requests.
 * Prefers using the session from auth context (more reliable) but falls back to querying Supabase.
 *
 * @param session - Optional session from auth context. If provided, uses this instead of querying Supabase.
 * @param apiVersion - Optional API version. Defaults to "api".
 * @returns Headers object with Authorization token
 * @throws Error if no session is available
 */
export const getAuthenticatedHeaders = async (session?: Session | null) => {
  let authSession: Session | null = session ?? null;

  // If no session provided, try to get from Supabase (fallback)
  if (!authSession) {
    const supabase = getSupabaseClient();
    const {
      data: { session: supabaseSession },
    } = await supabase.auth.getSession();
    authSession = supabaseSession;
  }

  // Check if session exists and has access_token
  if (!authSession || !authSession.access_token) {
    throw new Error("No active session found. Please sign in to continue.");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authSession.access_token}`,
  };
};

/**
 * Opens the authentication dialog using Office Dialog API
 * @param dialogUrl - URL of the authentication dialog page
 * @param onMessage - Callback to handle messages from the dialog
 * @param onClose - Callback to handle dialog close/cancellation
 * @returns Promise that resolves with the dialog object or rejects with error
 */
export const openAuthDialog = (
  dialogUrl: string,
  onMessage: (message: DialogMessage) => void,
  onClose?: (error?: Office.AsyncResult<Office.Dialog>) => void
): Promise<Office.Dialog> => {
  return new Promise((resolve, reject) => {
    const options: Office.DialogOptions = {
      height: 60,
      width: 40,
      displayInIframe: false,
    };

    Office.context.ui.displayDialogAsync(dialogUrl, options, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const dialog = result.value;

        // Handle messages from dialog
        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg: Office.DialogParentMessageReceivedEventArgs) => {
            try {
              const message: DialogMessage = JSON.parse(arg.message);
              onMessage(message);
            } catch (error) {
              console.error("Error parsing dialog message:", error);
              onMessage({
                status: "error",
                error: "Failed to parse dialog message",
              });
            }
          }
        );

        // Handle dialog close/cancellation
        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg: Office.DialogParentMessageReceivedEventArgs) => {
            if (onClose) {
              onClose(arg as any);
            }
            // Handle user cancellation (error code 12006)
            // Note: error code is in the event, check if available
            const errorCode = (arg as any).error;
            if (errorCode === 12006) {
              onMessage({
                status: "cancelled",
              });
            }
          }
        );

        resolve(dialog);
      } else {
        // Handle different error codes
        let errorMessage = "Unable to open authentication dialog. Please try again.";
        const errorCode = result.error?.code || result.error;
        if (errorCode === 12007) {
          errorMessage =
            "Authentication dialog blocked by popup blocker. Please allow popups for this site and try again.";
        } else if (errorCode === 12008) {
          errorMessage =
            "Authentication dialog blocked by security settings. Please check your browser settings.";
        }

        const error = new Error(errorMessage);
        reject(error);
      }
    });
  });
};

/**
 * Handles messages received from the authentication dialog
 * @param message - DialogMessage from the authentication dialog
 * @param dialog - Office.Dialog object to close on success
 * @param onSuccess - Callback when authentication succeeds (receives session)
 * @param onError - Callback when authentication fails (receives error message)
 */
export const handleDialogMessage = (
  message: DialogMessage,
  dialog: Office.Dialog,
  onSuccess: (session: import("../types/Authentication").AuthenticationSession) => void,
  onError: (error: string) => void
): void => {
  if (message.status === "success" && message.session) {
    // Close dialog on success
    dialog.close();
    onSuccess(message.session);
  } else if (message.status === "error" && message.error) {
    onError(message.error);
  } else if (message.status === "cancelled") {
    // User cancelled - not an error, just return to unauthenticated state
    dialog.close();
  }
};
